import React from 'react';
import { Split as SplitIcon, Plus, Trash2 } from 'lucide-react';
import QBDealPicker, { QBDealOption } from './QBDealPicker';
import { TransactionSplit } from '../../types';
import { round2 } from '../../utils/transactionSplits';
import './TransactionSplitEditor.css';

export interface TransactionSplitEditorProps {
  txnTotal: number;
  slices: TransactionSplit[];
  allocated: number;
  error: string | null;
  dealOptions: QBDealOption[];
  formatCurrencyFull: (n: number) => string;
  onUpdateSlice: (idx: number, patch: Partial<TransactionSplit>) => void;
  onRemoveSlice: (idx: number) => void;
  onAddSlice: () => void;
  onSave: () => void;
  onClear?: () => void;
  onCancel: () => void;
  saving: boolean;
  saveLabel?: string;
}

const TransactionSplitEditor: React.FC<TransactionSplitEditorProps> = ({
  txnTotal,
  slices,
  allocated,
  error,
  dealOptions,
  formatCurrencyFull,
  onUpdateSlice,
  onRemoveSlice,
  onAddSlice,
  onSave,
  onClear,
  onCancel,
  saving,
  saveLabel = 'Save split',
}) => {
  const remainder = round2(txnTotal - allocated);
  const remainderTone =
    Math.abs(remainder) < 0.01 ? 'ok' : remainder < 0 ? 'over' : 'partial';

  return (
    <div className="qbtx-split-editor">
      <div className="qbtx-split-head">
        <div className="qbtx-split-title">
          <SplitIcon size={13} /> Split this transaction
        </div>
        <div className="qbtx-split-totals">
          <span>Total {formatCurrencyFull(txnTotal)}</span>
          <span>· Allocated <strong>{formatCurrencyFull(allocated)}</strong></span>
          <span className={`qbtx-split-remainder qbtx-split-remainder-${remainderTone}`}>
            · {remainderTone === 'ok'
              ? 'Balanced'
              : remainderTone === 'partial'
              ? `Remaining ${formatCurrencyFull(remainder)} (not attributed here)`
              : `Over by ${formatCurrencyFull(Math.abs(remainder))}`}
          </span>
        </div>
      </div>

      <div className="qbtx-split-slices">
        {slices.map((s, idx) => {
          const remainingForFill = round2(remainder + (Number(s.amount) || 0));
          return (
            <div key={idx} className="qbtx-split-slice">
              <QBDealPicker
                value={s.dealId || ''}
                dealOptions={dealOptions}
                emptyLabel="— Unallocated (e.g. tax / fees) —"
                onChange={dealId => onUpdateSlice(idx, { dealId: dealId || null })}
                aria-label={`Slice ${idx + 1} deal`}
              />
              <div className="qbtx-split-amount">
                <span className="qbtx-split-amount-prefix">$</span>
                <input
                  type="number"
                  inputMode="decimal"
                  step="0.01"
                  min="0"
                  value={s.amount || ''}
                  onChange={e => onUpdateSlice(idx, { amount: parseFloat(e.target.value) || 0 })}
                  placeholder="0.00"
                  aria-label={`Slice ${idx + 1} amount`}
                />
                {Math.abs(remainingForFill - (Number(s.amount) || 0)) > 0.01 && remainingForFill > 0 && (
                  <button
                    type="button"
                    className="qbtx-split-fill"
                    onClick={() => onUpdateSlice(idx, { amount: remainingForFill })}
                    title={`Fill with the unallocated remainder (${formatCurrencyFull(remainingForFill)})`}
                  >
                    Fill
                  </button>
                )}
              </div>
              <input
                type="text"
                className="qbtx-split-note"
                value={s.note || ''}
                onChange={e => onUpdateSlice(idx, { note: e.target.value })}
                placeholder="Note (optional)"
                aria-label={`Slice ${idx + 1} note`}
              />
              <button
                type="button"
                className="qbtx-split-remove"
                onClick={() => onRemoveSlice(idx)}
                disabled={slices.length <= 1}
                title="Remove this slice"
                aria-label="Remove slice"
              >
                <Trash2 size={12} />
              </button>
            </div>
          );
        })}
      </div>

      <div className="qbtx-split-actions">
        <button type="button" className="qbtx-split-add" onClick={onAddSlice}>
          <Plus size={12} /> Add another slice
        </button>
        <div className="qbtx-split-action-right">
          {error && <span className="qbtx-split-error">{error}</span>}
          {onClear && (
            <button type="button" className="qbtx-mini-btn qbtx-mini-btn-danger" onClick={onClear} disabled={saving}>
              Clear split
            </button>
          )}
          <button type="button" className="qbtx-mini-btn" onClick={onCancel} disabled={saving}>
            Cancel
          </button>
          <button
            type="button"
            className="qbtx-mini-btn qbtx-mini-btn-primary"
            onClick={onSave}
            disabled={saving || remainder < -0.01}
          >
            {saving ? 'Saving…' : saveLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TransactionSplitEditor;
